/**
 * Conteúdo da paleta "Ir para…": recentes, páginas do menu e os atalhos de
 * criar que já existem como botão ou aba nas telas. Tudo passa pelo menu da
 * pessoa (`visibleMenuItems`, já sem o que o papel e as flags dela escondem):
 * o que não é para ela não aparece aqui. O menu inclui os itens `asideHidden`
 * (o catálogo `/padroes`), que aparecem aqui sem estar no aside.
 */
import {AppMenuItem} from '../aside/appMenu';
import {resolveSection} from './breadcrumbTrail';
import {RecentPage} from './recentPages';
import {matchesQuery} from './text';

export type CommandGroup = 'recentes' | 'paginas' | 'criar';

export type CommandItem = {
  id: string;
  group: CommandGroup;
  title: string;
  /** Seção do menu, mostrada ao lado e usada na busca. */
  context?: string;
  icon: string;
  to: string;
};

export const COMMAND_GROUP_LABELS: Record<CommandGroup, string> = {
  recentes: 'Recentes',
  paginas: 'Páginas',
  criar: 'Criar',
};

const GROUP_ORDER: CommandGroup[] = ['recentes', 'paginas', 'criar'];

export type CreateAction = {
  title: string;
  to: string;
  /** Item do menu de onde o botão é: sem ele no menu, sem o atalho. */
  sectionTo: string;
  /** Flag que mostra o botão na tela, quando há. */
  flag?: string;
};

/**
 * Os botões e abas de criar que existem hoje, com o mesmo destino. "Novo
 * ticket" abre o formulário do botão do Tickets pelo `?novo=1`. "Novo
 * fornecedor" é o botão da lista de fornecedores em Ops-Next: só com a flag
 * dela (a lista antiga não tem o botão).
 */
export const CREATE_ACTIONS: CreateAction[] = [
  {
    title: 'Nova solicitação de cotação',
    to: '/quotes/quote-requests/new',
    sectionTo: '/quotes/workspace',
  },
  {title: 'Novo ticket', to: '/tickets?novo=1', sectionTo: '/tickets'},
  {
    title: 'Novo fornecedor',
    to: '/providers/new',
    sectionTo: '/providers/list',
    flag: 'show-provider-pages-ops-next',
  },
  {title: 'Nova oficina', to: '/mechanics/new', sectionTo: '/mechanics/list'},
  {
    title: 'Convidar mecanizado',
    to: '/members/new',
    sectionTo: '/members/list',
  },
  {
    title: 'Novo cupom',
    to: '/coupons/new',
    sectionTo: '/coupons/list',
    flag: 'show-new-coupon-tab',
  },
  {
    title: 'Nova campanha',
    to: '/dialer/upload',
    sectionTo: '/dialer/campaigns',
  },
];

export const RECENT_IN_PALETTE = 5;

export const buildCommandItems = ({
  menu,
  recents,
  currentPath,
  flags = {},
}: {
  menu: AppMenuItem[];
  recents: RecentPage[];
  currentPath: string;
  flags?: Record<string, boolean>;
}): CommandItem[] => {
  const recentItems = recents
    // Recente de página que saiu do menu da pessoa não volta pela paleta
    .filter(
      ({path}) => path !== currentPath && Boolean(resolveSection(path, menu))
    )
    .slice(0, RECENT_IN_PALETTE)
    .map<CommandItem>(({path, title, section}) => ({
      id: `recente:${path}`,
      group: 'recentes',
      title,
      context: section,
      icon: 'bi-clock-history',
      to: path,
    }));

  const pageItems = menu.map<CommandItem>(({to, title, fontIcon}) => ({
    id: `pagina:${to}`,
    group: 'paginas',
    title,
    icon: fontIcon,
    to,
  }));

  const createItems = CREATE_ACTIONS.flatMap<CommandItem>(
    ({title, to, sectionTo, flag}) => {
      const section = menu.find((item) => item.to === sectionTo);
      if (!section || (flag && !flags[flag])) return [];
      return [
        {
          id: `criar:${to}`,
          group: 'criar',
          title,
          context: section.title,
          icon: 'bi-plus-lg',
          to,
        },
      ];
    }
  );

  return [...recentItems, ...pageItems, ...createItems];
};

/** Busca sem acento e sem diferenciar maiúscula, por título e seção. */
export const filterCommandItems = (items: CommandItem[], query: string) => {
  const matched = items.filter(({title, context}) =>
    matchesQuery(query, title, context)
  );
  return GROUP_ORDER.flatMap((group) =>
    matched.filter((item) => item.group === group)
  );
};
